// The back-office shell: the BatiFlow shell of @batiflow/ui (app bar on the side, bottom bar on
// phones) with a header of its own (no global search, no organisation switcher: an admin works
// across organisations). The header carries the brand, the session-protection hint, the language
// switcher and sign-out.
import type { AppLocale } from '@batiflow/i18n';
import {
  AppBar,
  AppShell,
  BatiFlowLogo,
  createShellStore,
  layout,
  ShortcutProvider,
  resolveTheme,
} from '@batiflow/ui';
import {
  Avatar,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  __css,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuGroupHeader,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
  tokens,
} from '@fluentui/react-components';
import {
  LocalLanguage20Regular,
  LockClosed16Regular,
  SignOut20Regular,
} from '@fluentui/react-icons';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { useRuntime } from '@/app/providers/runtime-provider';
import { useAdminSession } from '@/app/providers/session-provider';
import { useThemePreference } from '@/app/providers/theme-provider';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { BACKOFFICE_CANVAS_LIGHT } from './canvas';
import { navItems, pageOfPath } from './nav';

const useStyles = __css({"tint":{"mc9l5x":"f1bxpd7w"},"header":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","st59p4":"f1hxgz52","Bnnss6s":"fi64zpg","B7ck84d":"f1ewtqcl","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","De3pzq":"f16xq7d1"},"tag":{"sj55zd":"fkfq4zb","Bhrd7zp":"fl43uef","Huce71":"fz5stix"},"spacer":{"Bh6795r":"fqerorx"},"hint":{"mc9l5x":"ftuwxu6","Bt984gj":"f122n59","i8kkvl":"f1ufnopg","sj55zd":"fkfq4zb","Bgto7am":"fuzgk9b","Bcyd06e":"f16j95f1"},"hintText":{"B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","ygn44y":"f1cmbuwj","Huce71":"fz5stix"},"hintIcon":{"Bnnss6s":"fi64zpg"},"actions":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1ufnopg"},"name":{"D305fb":"f1chayzo","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp","ygn44y":"f1cmbuwj","Huce71":"fz5stix"}});

const LANGUAGES: readonly AppLocale[] = ['fr', 'ar', 'en'];

export function BackofficeLayout({ children }: { children: ReactNode }) {
  const styles = useStyles();
  const { t } = useTranslation('backoffice');
  const { t: tUi } = useTranslation('ui');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const runtime = useRuntime();
  const { me } = useAdminSession();
  const locale = useLocale();
  const shellStore = useMemo(() => createShellStore(runtime.storage), [runtime.storage]);
  const { preference } = useThemePreference();
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const prefersHighContrast = useMediaQuery('(forced-colors: active)');
  const lightTheme = resolveTheme(preference, { prefersDark, prefersHighContrast }) === 'light';
  const [signOutFailed, setSignOutFailed] = useState(false);
  const items = useMemo(() => navItems(t), [t]);

  if (!me) return null;
  const { displayName } = me.admin;

  return (
    <ShortcutProvider>
      <div
        className={styles.tint}
        style={
          lightTheme
            ? { ['--colorNeutralBackground3' as string]: BACKOFFICE_CANVAS_LIGHT }
            : undefined
        }
      >
        <AppShell
          store={shellStore}
          header={
            <header className={styles.header}>
              <BatiFlowLogo />
              <span className={styles.tag}>{t('header.tag')}</span>
              <span className={styles.spacer} />
              <span className={styles.hint}>
                <LockClosed16Regular className={styles.hintIcon} aria-hidden />
                <Caption1 className={styles.hintText}>{t('header.sessionHint')}</Caption1>
              </span>
              <div className={styles.actions}>
                <Menu
                  checkedValues={{ language: [locale] }}
                  onCheckedValueChange={(_event, data) => {
                    const next = LANGUAGES.find((value) => value === data.checkedItems[0]);
                    if (next) runtime.localeStore.setLocale(next);
                  }}
                >
                  <MenuTrigger disableButtonEnhancement>
                    <Tooltip content={t('header.language')} relationship="label">
                      <Button appearance="subtle" icon={<LocalLanguage20Regular />} />
                    </Tooltip>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      {LANGUAGES.map((value) => (
                        <MenuItemRadio key={value} name="language" value={value}>
                          {tUi(`shell.languages.${value}`)}
                        </MenuItemRadio>
                      ))}
                    </MenuList>
                  </MenuPopover>
                </Menu>
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Tooltip
                      content={t('header.accountMenu', { name: displayName })}
                      relationship="label"
                    >
                      <Button appearance="subtle" shape="circular">
                        <Avatar name={displayName} size={28} aria-hidden />
                      </Button>
                    </Tooltip>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuGroup>
                        <MenuGroupHeader>{displayName}</MenuGroupHeader>
                        <MenuGroupHeader>{t('header.sessionHint')}</MenuGroupHeader>
                      </MenuGroup>
                      <MenuDivider />
                      <MenuItem
                        icon={<SignOut20Regular />}
                        onClick={() => {
                          void runtime.signOut().then((ok) => {
                            // Open after the menu has finished closing: a dialog opened while the
                            // menu's focus management is still active would be hidden from
                            // screen readers.
                            if (!ok)
                              setTimeout(() => {
                                setSignOutFailed(true);
                              }, 50);
                          });
                        }}
                      >
                        {t('header.signOut')}
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </div>
            </header>
          }
          appBar={
            <AppBar
              items={items}
              activeId={pageOfPath(pathname)}
              onNavigate={(item) => {
                void navigate({ to: item.href });
              }}
            />
          }
        >
          {children}
        </AppShell>
      </div>
      <Dialog
        open={signOutFailed}
        modalType="alert"
        onOpenChange={(_event, data) => {
          setSignOutFailed(data.open);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('signOut.title')}</DialogTitle>
            <DialogContent>{t('signOut.failed')}</DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => {
                  setSignOutFailed(false);
                }}
              >
                {t('signOut.close')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </ShortcutProvider>
  );
}


import "/opt/batiflow/app/apps/backoffice/src/app/shell/backoffice-layout.griffel.css";